import { FC, useState } from 'react';
import {
  Badge,
  FaIcon,
  Filter,
  Sheet,
  TFilterConfig,
  useFilterChips,
} from '@fe-learner-common';
import { faBarsFilter } from '@fortawesome/pro-regular-svg-icons/faBarsFilter';
import { useIntl } from 'react-intl';
import { TW_ROOT } from '@utils/constants';
import { TCatalogFilter } from 'types/filter';
import { publishAnalyticsEvent } from '@utils/publishAnalyticsEvent';
import { serializeActiveFilters } from '@routes/catalog/hooks/useCatalogFilter';

type FilterViewProps = {
  filter: TCatalogFilter;
  setFilter: (filter: TCatalogFilter) => void;
  filterConfig: TFilterConfig | null;
  clearFilter: () => void;
  nrOfResults?: number;
};

export const FilterView: FC<FilterViewProps> = ({
  filter,
  setFilter,
  filterConfig,
  clearFilter,
  nrOfResults,
}) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const filterChips = useFilterChips(filterConfig);

  const handleFilterChange = (key: string, value: Set<string> | string) => {
    const newFilter = {
      ...filter,
      [key]: value,
    };

    setFilter({ ...newFilter });
  };

  const publishFilterChangeAnalytics = () => {
    const appliedFilter = serializeActiveFilters(filter);

    if (Object.keys(appliedFilter).length > 0) {
      publishAnalyticsEvent(
        { context: 'CatalogFilter', action: 'Applied' },
        appliedFilter,
      );
    }
  };

  const handleOpenChange = (open: boolean) => {
    setOpen(open);

    if (open) {
      publishAnalyticsEvent({ context: 'CatalogFilter', action: 'Opened' });
    } else {
      publishFilterChangeAnalytics();
    }
  };

  const handleViewResults = () => {
    setOpen(false);
    publishFilterChangeAnalytics();
  };

  const Trigger = (
    <button
      className="l-btn l-btn-md l-btn-tertiary"
      onClick={() => setOpen(true)}
    >
      <FaIcon icon={faBarsFilter} height={16} width={16} />
      {intl.formatMessage({ id: 'learner.common.filter' })}
      {filterChips?.length > 0 ? (
        <Badge aria-hidden size="lg" type="negative" className="">
          {filterChips?.length}
        </Badge>
      ) : null}
    </button>
  );

  return (
    <Sheet
      container={document.getElementById(TW_ROOT) as HTMLElement}
      open={open}
      onOpenChange={handleOpenChange}
      sheetClassName="mt-16 w-[360px] sm:w-[640px] overflow-hidden"
      texts={{
        title: intl.formatMessage({ id: 'learner.common.filter' }),
        closeAriaLabel: intl.formatMessage({
          id: 'learner.common.close.ariaLabel',
        }),
      }}
      trigger={Trigger}
    >
      <Filter
        onClearFilter={clearFilter}
        onChangeFilter={handleFilterChange}
        onViewResults={handleViewResults}
        filterConfig={filterConfig}
        nrOfResults={nrOfResults}
        filterChips={filterChips}
      />
    </Sheet>
  );
};
